import React, { useEffect, useRef, useState } from "react";
import './Ticket.css'; // Assuming plain CSS file for styling
import axiosInstance from "../../interceptor/axiosInstance";
import {formatTime} from '../Utils/UtilityService.js';
import { setGlobalInterval, clearGlobalInterval } from "../Utils/IntervalManager.js";
import config from "../../config.js";
import { getWebSocket } from "../../webSocket/WebSocketService.js";


const OpenTicket = () => {
  const [chatList, setChatList] = useState([]); // State for storing ticket list
  const [selectedTicket, setSelectedTicket] = useState(null); // State for the selected ticket
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state
  const [isDisplayChat, setIsDisplayChat] = useState(false);
  const [messages, setMessages] = useState([]); // State for storing messages of the selected ticket
  const [ticket, setTicket] = useState({});
  const [userType, setUserType] = useState(null);
  const [ws, setWs] = useState(null);
  const selectedTicketRef = useRef(null); // Use ref to track selectedTicket
  const messagesEndRef = useRef(null);
  // Update ref whenever selectedTicket changes
  useEffect(() => {
    selectedTicketRef.current = ticket;
  }, [ticket]);
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  useEffect(() => {
    const socket = getWebSocket();
    setWs(socket);

    socket.onmessage = async (event) => {
        let message;
        if (event.data instanceof Blob) {
          message = await event.data.text();
      } else {
          message = event.data;
      }

      if (typeof message === 'string' && message.trim().startsWith('{')) {
          const resData = JSON.parse(message);

          if (selectedTicketRef.current?.thread_id === resData.threadId) {
              fetchMessages(selectedTicketRef.current?.thread_id, true);
          }
      } else {
          console.warn('Non-JSON WebSocket message received:', message);
      }
    };
    
    return () => {
        socket.onmessage = null; // Prevent memory leaks
    };
}, []);


  
  // Fetch tickets initially
  useEffect(() => {
    // Clear the previous interval before setting a new one
    clearGlobalInterval();

    const fetchChats = async (skipLoader = false) => {
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        if (user && user.role) {
          setUserType(user.role);
        }
        const response = await axiosInstance.get(`/api/ticket/open-ticket-list`,
          { skipLoader });
        setChatList(response.data); // Set the ticket list to state
        setSelectedTicket(response.data[0]); // Set the first ticket as selected by default
      } catch (err) {
        setError("Error fetching data"); // Handle error if request fails
      } finally {
        setLoading(false); // Stop loading after the request is complete
      }
    };

    fetchChats(false);

    // Set interval to refresh ticket list every 5 seconds
    const intervalId = setInterval(() => {
      fetchChats(true); // Skip loader for interval-based requests
    }, 5000); // Set interval to 3 seconds
    setGlobalInterval(intervalId); // Store the interval globally

    // Cleanup interval on component unmount
    return () => {
      clearGlobalInterval();
    };
  }, []); // Runs only on component mount

  // Handle loading and error states
  // if (loading) {
  //   return <div>Loading...</div>;
  // }

  if (error) {
    return <div>{error}</div>;
  }

  const fetchMessages = async (threadId,skipLoader = false) => {
    try {
      const response = await axiosInstance.get(`/api/ticket/get-conversations`,
      {
        params: { thread_id: threadId },skipLoader 
      });
      setMessages(response.data); // Set the messages for the selected ticket
      
    } catch (err) {
      setError("Error fetching messages"); // Handle error if request fails
    }
  };

  const handleRowClick = (ticket) => {
    setTicket(ticket);
    fetchMessages(ticket.thread_id);
    setIsDisplayChat(true);
  };

  const handleClosePopup = () => {
    setIsDisplayChat(false);
    setTicket({});
  };

  const handleAssignChat = () => {
    const assignChat = async () => {
      try {
        let payload = {
          "thread_id": ticket.thread_id
        };
        const response = await axiosInstance.post(`/agent-conversation/assign-to-agent`, payload);
        setMessages(response.data); // Set the messages for the selected ticket
        const updatedChatList = chatList.filter((chat) => chat.thread_id !== ticket.thread_id);
        setChatList(updatedChatList);

        if (ws) {
          ws.send(JSON.stringify({ type: 'message', ticketStatus:"assigned", content: response.data.message, threadId:ticket.thread_id }));
        }

      } catch (err) {
        setError("Error assigning the ticket"); // Handle error if request fails
      }
    };
    assignChat();
    handleClosePopup();
  };

  const statuses = ["waiting", "closed", "chatwithbot"];
  // Function to get the status text and color
  const getStatusText = (ticket) => {
    if (ticket === "waiting") return { text: "Waiting / Agent", color: "red" };
    if (ticket === "closed") return { text: "Closed", color: "green" };
    return { text: "Chat with Bot", color: "yellow" };
  };

  return (
    <div className="d-flex flex-column min-vh-86">
      <main className="flex-fill bg-light">
        {/* Status text container positioned at top right corner */}
        <span style={styles.statusTextContainer}>
          {statuses.map((ticket,index) => {
            const { text, color } = getStatusText(ticket);
            return (
              <div key={ticket.queue_id || `status-${index}`} style={styles.statusItem}>
                <span
                  style={{
                    ...styles.statusBullet,
                    backgroundColor: color,
                  }}
                ></span>
                <span style={styles.statusText}>{text}</span>
              </div>
            );
          })}
        </span>
        <div style={styles.tableContainer}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={styles.headerCell}>Customer Messages</th>
                <th style={styles.headerCell}>Status</th>
                <th style={styles.headerCell}>Created At</th>
              </tr>
            </thead>
            <tbody>
              {chatList.length == 0 && (
                <tr>
                <td style={styles.noRecordCell} colSpan="3">
                  No records found
                </td>
              </tr>
              )}
              {chatList.map((ticket,index) => (
                <tr
                  key={ticket.queue_id || `status-${index}`}
                  style={{
                    ...styles.row,
                    ...(ticket.waitingStatus === "waiting" && ticket.status === "pending"
                      ? styles.waitingColor
                      : ticket.status === "closed"
                      ? styles.closeColor
                      : styles.conversationColor),
                  }}
                  onClick={() => handleRowClick(ticket)}
                >
                  <td style={styles.cell}>{ticket.message}</td>
                  <td style={styles.cell}>{ticket.status}</td>
                  <td style={styles.cell}>
                    {new Date(ticket.created_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {isDisplayChat && (
          <div style={styles.overlay}>
            <div style={styles.popup}>
              <button
                style={styles.closeIcon}
                onClick={() => handleClosePopup()}
              >
                ×
              </button>
              <div style={styles.chatContainer}>
                <h4 style={styles.chatHeader}>
                  Conversation Details
                  {ticket.waitingStatus === "waiting" && ticket.status === "pending" && (
                    <button
                      style={styles.closeButton}
                      onClick={() => handleAssignChat()}
                    >
                      Assign To Me
                    </button>
                  )}
                </h4>
                <div style={styles.messagesContainer}>
                  {messages.length > 0 ? (
                    messages.map((message, index) => (
                      <div key={index}>
                        <div
                          style={styles.messageName}
                          className={`${
                            message.sender === "user"
                              ? "user-time"
                              : "support-time"
                          }`}
                        >
                          {message.sender === "user"
                            ? "client"
                            : message.sender === "bot"
                            ? "assistant"
                            : "you"}
                          , {formatTime(message.created_at)}
                        </div>
                        <div
                          className={`message ${
                            message.sender === "user"
                              ? "user-message"
                              : "support-message"
                          }`}
                          style={styles.message}
                        >
                          {message.message}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={styles.noMessages}>No messages to display</div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
                
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const styles = {
  tableContainer: {
    position: "relative", 
    top: "40px"
  },
  headerCell: {
    border: "1px solid #ddd",
    padding: "8px",
    backgroundColor: "#1A4164",
    textAlign: "left",
    fontWeight: "bold",
    color: "white",
  },
  cell: {
    border: "1px solid #ddd",
    padding: "8px",
    textAlign: "left",
  },
  row: {
    ':hover': { backgroundColor: '#f9f9f9' },
    cursor: "pointer",
  },
  waitingColor: {
    backgroundColor: "#f5c1ba",
  },
  closeColor: {
    backgroundColor: "#DAF7A6", 
  },
  conversationColor: {
    backgroundColor: "#F4F49B", 
  },
  statusTextContainer: {
    position: "absolute",
    top: "65px",
    right: "30px",
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-end",
  },
  statusItem: {
    display: "flex",
    alignItems: "center",
    margin: "5px",
  },
  statusBullet: {
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    marginRight: "8px",
  },
  statusText: {
    fontSize: "14px",
  },
  popup: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    padding: "20px",
    border: "1px solid #ccc",
    backgroundColor: "#f9f9f9",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
    zIndex: 1000,
    width: "80%"
  },
  overlay: {
    position: "fixed",
    top: "0",
    left: "0",
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 999,
  },
  closeIcon: {
    position: "absolute",
    top: "-10px",
    right: "0px",
    background: "none",
    border: "none",
    fontSize: "26px",
    color: "#333",
    cursor: "pointer",
    fontWeight: "bold",
  },
  chatContainer: {
    display: "flex",
    flexDirection: "column",
    height: "75vh", 
    border: "1px solid #ccc",
    backgroundColor: "#f9f9f9",
    // padding: "15px",
    // borderRadius: "8px",
    // overflowY: "auto",
  },
  chatHeader: {
    position: "relative", // Allow absolute positioning for the button
    padding: "5px",
    backgroundColor: "#1A4164",
    borderBottom: "1px solid #ccc",
    textAlign: "center",
    color: "white",
  },
  messagesContainer: {
    flex: 1, // Allow this section to grow and shrink
    overflowY: "auto", // Scroll if content overflows
    padding: "10px",
  },
  message: {
    marginBottom: "15px",
  },
  noMessages: {
    textAlign: "center",
    color: "#666",
    padding: "20px",
  },
  closeButton: {
    position: "absolute", // Position button absolutely
    right: "5px", // Place it on the right
    top: "50%", // Vertically align it
    transform: "translateY(-50%)", // Center perfectly vertically
    padding: "5px 10px",
    backgroundColor: "#051C3E",
    border: "1px solid #ccc",
    borderRadius: "4px",
    cursor: "pointer",
    color: "white",
    fontSize: "18px",
  },
  messageName: {
    // position: "absolute",
    top: "-20px", // Name above the box
    left: "0",
    fontSize: "12px",
    fontWeight: "normal",
    color: "#888",
  },
  noRecordCell: {
    padding: "10px",
    textAlign: "left",
    color: "#888",
    border: "1px solid #ddd",
  },
};

export default OpenTicket;
